import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link, useParams } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Container } from 'dashboard/layouts/IndexViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { InquiryTemplate, InquiryTemplateStatus } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { ThemeSet, ThemeSetAttributes } from 'dashboard/models/theme-set';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { setInquiryTemplateVersion, setThemeSet } from 'dashboard/redux/slices/theme-set';
import { RootState } from 'dashboard/redux/store';
import { loadInquiryTemplatesList } from 'dashboard/redux/thunk-actions/inquiry-templates';
import {
  loadPartialStepsPreview,
  loadStylesMap,
  LoadStylesMapParams,
  loadThemeSet,
  loadThemeSetInquiryTemplateVersion,
  loadThemeSetStepsPreview,
  updateThemeSet,
} from 'dashboard/redux/thunk-actions/theme-set';
import DashboardThemeSetsEditorNewView from 'dashboard/views/DashboardThemeSetsEditorNewView';
import DashboardThemeSetsEditorView from 'dashboard/views/DashboardThemeSetsEditorView';
import { partitionInUseInquiryTemplates } from 'dashboard/views/DashboardThemeSetsEditorView/util';
import Spacer from 'design-system/components/Spacer';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

import 'lib/initializers/i18n';

export const getLatestVersionId = (inquiryTemplate: InquiryTemplate): string | undefined =>
  inquiryTemplate.relationships['latest-draft-version']?.data?.id ??
  inquiryTemplate.relationships['latest-published-version']?.data?.id;

const CenteredView = styled.div`
  display: flex;
  min-height: 80vh;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

type Params = { themeSetId: string };

const DashboardThemeSetsEditorRoute = (props: PropsFromRedux) => {
  const params = useParams<Params>() as Params; // themeSetId will always be defined

  // Clear out any stale theme set and ITV
  useOnMountAndUnmount(() => {
    props.setThemeSet(null);
    props.setInquiryTemplateVersion(null);
  });

  const displayNetworkErrorMessage = () =>
    props.showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);

  // Set the initially selected template preview to be the first in-use-ITV if available.
  // Otherwise use the first not-in-use-ITV.
  useEffect(() => {
    if (props.inquiryTemplates.length > 0 && props.themeSet != null) {
      const [inUseInquiryTemplates, notInUseInquiryTemplates] = partitionInUseInquiryTemplates(
        props.themeSet,
        props.inquiryTemplates,
        {
          // Since the "Inquiry Templates Preview" dropdown in DashboardThemeSetEditorView
          // will display the template options sorted, make sure that we are grabbing the first
          // in use/not in use option from the sorted list.
          sortByNameDisplay: true,
        },
      );
      const inquiryTemplateVersionId = getLatestVersionId(
        inUseInquiryTemplates[0] || notInUseInquiryTemplates[0],
      );
      if (inquiryTemplateVersionId) {
        handleInquiryTemplateChange(inquiryTemplateVersionId);
      }
    }
  }, [props.inquiryTemplates, props.themeSet]);

  const { loading: loadingITandThemeSet } = useApiLoader({
    loader: async () => {
      await Promise.all([
        props.loadInquiryTemplatesList({
          currentPage: 1,
          pageSize: 100,
          sortParams: { sort: '-', shouldSortDescending: false },
          filters: { status: [InquiryTemplateStatus.Active] },
          dynamicFlowOnly: true,
        }),
        props.loadThemeSet(params.themeSetId),
      ]);
    },
    deps: [props.loadInquiryTemplatesList, props.showMessage],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load required data. Please retry by refreshing the page',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const { callback: handleInquiryTemplateChange, error: handleInquiryTemplateChangeError } =
    useApiCallback(
      async (inquiryTemplateVersionId: string) => {
        await Promise.all([
          props.loadThemeSetInquiryTemplateVersion(inquiryTemplateVersionId),
          props.loadThemeSetStepsPreview(inquiryTemplateVersionId, props.locale),
        ]);
      },
      [props.loadThemeSetInquiryTemplateVersion, props.loadThemeSetStepsPreview],
    );

  useApiErrorHandler({
    error: handleInquiryTemplateChangeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load template for preview. Please retry by refreshing the page.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });
  const { callback: handleLocaleChange, error: handleLocaleChangeError } = useApiCallback(
    async (locale: InquiryIso6391LanguageCode) => {
      if (props.inquiryTemplateVersion) {
        await props.loadThemeSetStepsPreview(props.inquiryTemplateVersion.id, locale);
      }
    },
    [props.loadThemeSetStepsPreview, props.inquiryTemplateVersion],
  );

  useApiErrorHandler({
    error: handleLocaleChangeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load preview for specified language. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const { callback: handleUpdateThemeSet, error: handleUpdateThemeSetError } = useApiCallback(
    async (themeSet: ThemeSet, themeSetAttributes: Partial<ThemeSetAttributes>) => {
      await props.updateThemeSet(themeSet, themeSetAttributes);
    },
    [props.updateThemeSet],
  );

  useApiErrorHandler({
    error: handleUpdateThemeSetError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to update Theme. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const { callback: handleThemeAttributesChange, error: handleThemeAttributesChangeError } =
    useApiCallback(
      async (loadStyleMapParam: LoadStylesMapParams) => {
        await props.loadStylesMap(loadStyleMapParam);
      },
      [props.loadThemeSetInquiryTemplateVersion],
    );

  useApiErrorHandler({
    error: handleThemeAttributesChangeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update theme preview. Please retry by refreshing the page.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const {
    callback: handleLoadPartialThemeSetStepsPreview,
    error: handleLoadPartialThemeSetStepsPreviewError,
  } = useApiCallback(
    async (
      inquiryTemplateVersion: InquiryTemplateVersion,
      themeSet: ThemeSet,
      startPaginationIndex: number,
      endPaginationIndex: number,
    ) => {
      await props.loadPartialStepsPreview(
        inquiryTemplateVersion.id,
        props.locale,
        themeSet,
        startPaginationIndex,
        endPaginationIndex,
      );
    },
    [props.loadPartialStepsPreview],
  );

  useApiErrorHandler({
    error: handleLoadPartialThemeSetStepsPreviewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update theme preview. Please retry by refreshing the page.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: displayNetworkErrorMessage,
  });

  const useThemeSetEditorV2 = useFeatureFlag('dashboard:fle-enable-theme-set-with-new-editor');

  if (!loadingITandThemeSet && props.inquiryTemplates.length === 0) {
    return (
      <CenteredView>
        <Spacer height={3} />
        <div>You need at least one Inquiry Template to preview in Theme editor.</div>
        <Spacer height={5} />
        <Link to='/dashboard/inquiry-templates'>Go to Inquiry Templates page</Link>
      </CenteredView>
    );
  } else if (props.inquiryTemplateVersion == null || props.themeSet == null) {
    return (
      <Container>
        <LoadingIndicator />
      </Container>
    );
  } else if (useThemeSetEditorV2) {
    return (
      <DashboardThemeSetsEditorNewView
        inquiryTemplates={props.inquiryTemplates}
        inquiryTemplateVersion={props.inquiryTemplateVersion as InquiryTemplateVersion}
        themeSet={props.themeSet as ThemeSet}
        stepsPreview={props.stepsPreview}
        locale={props.locale}
        styleMap={props.styleMap}
        handleUpdateThemeSet={handleUpdateThemeSet}
        handleInquiryTemplateChange={handleInquiryTemplateChange}
        handleLocaleChange={handleLocaleChange}
        handlePreviewChange={handleLoadPartialThemeSetStepsPreview}
      />
    );
  } else {
    return (
      <DashboardThemeSetsEditorView
        inquiryTemplates={props.inquiryTemplates}
        inquiryTemplateVersion={props.inquiryTemplateVersion as InquiryTemplateVersion}
        themeSet={props.themeSet as ThemeSet}
        stepsPreview={props.stepsPreview}
        locale={props.locale}
        styleMap={props.styleMap}
        handleUpdateThemeSet={handleUpdateThemeSet}
        handleInquiryTemplateChange={handleInquiryTemplateChange}
        handleLocaleChange={handleLocaleChange}
        handleThemeAttributesChange={handleThemeAttributesChange}
      />
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    inquiryTemplates: state.inquiryTemplates.inquiryTemplates,
    inquiryTemplateVersion: state.themeSet.inquiryTemplateVersion,
    themeSet: state.themeSet.themeSet,
    stepsPreview: state.themeSet.stepsPreview,
    locale: state.themeSet.locale,
    styleMap: state.themeSet.stylesMap,
  };
};

const mapDispatchToProps = {
  loadInquiryTemplatesList,
  loadThemeSetInquiryTemplateVersion,
  loadPartialStepsPreview,
  loadStylesMap,
  loadThemeSet,
  updateThemeSet,
  loadThemeSetStepsPreview,
  clearMessages,
  showMessage,
  setThemeSet,
  setInquiryTemplateVersion,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardThemeSetsEditorRoute);
